import { createSelector } from '@ngrx/store';
import {
  convertDistance,
  getCompassDirection,
  getGreatCircleBearing,
  getPreciseDistance,
} from 'geolib';
import { Record, Seq } from 'immutable';
import { round } from 'lodash-es';
import { TripLegStatus, WaypointType } from 'src/graphql/generated/graphql.generated';
import { selectEstimatedDurationsByTripId } from 'src/store/entityStores/estimated-duration-selectors';
import {
  selectCurrentTripLegByTripId,
  selectNonCancelledTripLegsByTripId,
} from 'src/store/entityStores/tripleg-selectors';
import { selectVehicleDispositionByTripId } from 'src/store/entityStores/vehicle-disposition-selector';
import { selectWaypointsWithLatLngsByTripId } from 'src/store/entityStores/waypoint-selectors';
import { AddressRecord } from 'src/store/generated/address-record.generated';
import {
  CoordinatesProps,
  CoordinatesRecord,
} from 'src/store/generated/coordinates-record.generated';
import { EstimatedDurationRecord } from 'src/store/generated/estimated-duration-record.generated';
import { TripLegRecord } from 'src/store/generated/trip-leg-record.generated';
import { VehicleDispositionRecord } from 'src/store/generated/vehicle-disposition-record.generated';
import { WaypointRecord } from 'src/store/generated/waypoint-record.generated';
import { hasLatLng } from 'src/store/model-helpers';

export type TripLegStyles = {
  [Status in TripLegStatus]: google.maps.PolylineOptions;
};
export const emptyWaypoint = new WaypointRecord({
  id: 'fake',
  type: WaypointType.ReceivingFacility,
  name: '',
  abbreviation: '',
  address: new AddressRecord({
    streetAddress: null,
    city: null,
    country: null,
    county: null,
    postalCode: null,
    region: null,
    gpsId: null,
    coordinates: new CoordinatesRecord({ latitude: null, longitude: null }),
  }),
  companyId: 'fake',
  arrivalTime: new Date(),
  dateLastUpdated: new Date(),
  dateCreated: new Date(),
  dateDeleted: null,
  departureTime: new Date(),
  locationId: 'fake',
  timezoneCd: 'fake',
  isRotorWingLz: true,
  estimatedDurationId: null,
  currentEtaId: null,
  isPatientPickup: false,
  isPatientDropoff: false,
  arrivingType: null,
  lastUpdatedById: null,
});

export function withCoordinates(
  waypoint: WaypointRecord,
  coordinates: { latitude: number; longitude: number },
) {
  return waypoint.withMutations((w) => {
    w.setIn(['address', 'coordinates', 'latitude'], coordinates.latitude);
    w.setIn(['address', 'coordinates', 'longitude'], coordinates.longitude);
  });
}

export function fakeWaypoint(lat: number, long: number, type: WaypointType) {
  return withCoordinates(
    emptyWaypoint.withMutations((w) => {
      w.set('type', type);
    }),
    { latitude: lat, longitude: long },
  );
}

const lineIcon = (strokeColor: string) => {
  return {
    icon: {
      path: 'M 0,-1 0,1',
      strokeOpacity: 1,
      scale: 4,
      strokeColor,
    },
    offset: '0',
    repeat: '20px',
  };
};
export const tripLegStyles: TripLegStyles = {
  Cancelled: {
    strokeOpacity: 0,
    strokeColor: 'crimson',
    icons: [lineIcon('crimson')],
  },
  Complete: { strokeColor: 'darkgreen' },
  EnRoute: { strokeColor: 'chartreuse' },
  Planned: {
    strokeOpacity: 0,
    icons: [lineIcon('cadetblue')],
    strokeColor: 'cadetblue',
  },
};

export class TripLegViewModelRecord extends Record({
  startingWaypoint: null as any,
  tripLeg: null as any,
  endingWaypoint: null as any,
  isCurrent: null as any,
  isNext: null as any,
  isNextAtWaypoint: null as any,
  isLast: null as any,
  isFirst: null as any,
  officialDuration: null as any,
  calculatedDuration: null as any,
}) {
  public startingWaypoint: WaypointRecord;
  public tripLeg: TripLegRecord;
  public endingWaypoint: WaypointRecord;
  public isCurrent: boolean;
  public isNext: boolean;
  public isNextAtWaypoint: boolean;
  public isLast: boolean;
  public isFirst: boolean;
  public officialDuration: EstimatedDurationRecord | undefined;
  public calculatedDuration: EstimatedDurationRecord | undefined;

  constructor(params: {
    startingWaypoint: WaypointRecord;
    tripLeg: TripLegRecord;
    endingWaypoint: WaypointRecord;
    isCurrent: boolean;
    isNext: boolean;
    isNextAtWaypoint: boolean;
    isLast: boolean;
    isFirst: boolean;
    officialDuration: EstimatedDurationRecord | undefined;
    calculatedDuration: EstimatedDurationRecord | undefined;
  }) {
    super(params);
  }

  // eslint-disable-next-line id-blacklist
  get number(): number {
    return this.tripLeg.sequence + 1;
  }

  get polylineOptions(): google.maps.PolylineOptions {
    return tripLegStyles[this.tripLeg.status];
  }

  get latLngs(): { lat: number; lng: number }[] {
    const decodePath = google.maps.geometry?.encoding?.decodePath;
    if (this.tripLeg?.encodedPolyline != null && decodePath != null) {
      const path: google.maps.LatLng[] = decodePath(this.tripLeg.encodedPolyline);
      if (path != null && path.length > 0) {
        return path.map((_) => {
          const lat = _.lat();
          const lng = _.lng();
          return { lat, lng };
        });
      }
    }
    return [this.startingLatLng, this.endingLatLng];
  }

  get startingLatLng() {
    if (this.startingWaypoint.address == null) {
      throw new Error('address for starting waypoint is null');
    } else if (this.startingWaypoint.address.coordinates == null) {
      throw new Error('coordinates for starting waypoint is null');
    }
    return getLatLng(this.startingWaypoint.address.coordinates);
  }

  get endingLatLng() {
    if (this.endingWaypoint.address == null) {
      throw new Error('address for ending waypoint is null');
    } else if (this.endingWaypoint.address.coordinates == null) {
      throw new Error('coordinates for ending waypoint is null');
    }
    return getLatLng(this.endingWaypoint.address.coordinates);
  }

  get distance() {
    return getPreciseDistance(this.startingLatLng, this.endingLatLng);
  }

  get miles() {
    return round(convertDistance(this.distance, 'mi'), 0);
  }

  get nauticalMiles() {
    return round(convertDistance(this.distance, 'sm'), 0);
  }

  get direction() {
    return getCompassDirection(this.startingLatLng, this.endingLatLng);
  }

  get bearing() {
    return Math.floor(getGreatCircleBearing(this.startingLatLng, this.endingLatLng));
  }

  get isStarted() {
    return this.tripLeg.status === TripLegStatus.EnRoute;
  }

  get isComplete() {
    return this.tripLeg.status === TripLegStatus.Complete;
  }

  get duration(): number | null {
    return null;
    // TODO: uncomment when date differences are fixed.
    // const diff = this.isStarted
    //   ? dateFns.differenceInMinutes(
    //       Date.now() + 1000 * 60 * new Date().getTimezoneOffset(),
    //       this.startingWaypoint.departureTime!
    //     )
    //   : this.isComplete
    //     ? dateFns.differenceInMinutes(
    //         this.endingWaypoint.arrivalTime!,
    //         this.startingWaypoint.departureTime!
    //       )
    //     : null;
    // return diff;
  }

  get waypoints() {
    return [this.startingWaypoint, this.endingWaypoint];
  }
}

export function getLatLng(
  coordinates:
    | CoordinatesProps
    | { coordinates: CoordinatesProps }
    | { address: { coordinates: CoordinatesProps | null } | null },
  defaults?: {
    latitude: number;
    longitude: number;
  },
): {
  latitude: number;
  longitude: number;
  lat: number;
  lng: number;
} {
  {
    const maybeCoord = coordinates as any;
    const { latitude, longitude } = maybeCoord;
    if (latitude != null && longitude != null) {
      return {
        latitude,
        longitude,
        lat: latitude,
        lng: longitude,
      };
    }
    if (maybeCoord.coordinates != null) {
      return getLatLng(maybeCoord.coordinates, defaults);
    }
    if (maybeCoord.address != null) {
      return getLatLng(maybeCoord.address.coordinates, defaults);
    }
    if (defaults != null) {
      return {
        ...defaults,
        lat: defaults.latitude,
        lng: defaults.longitude,
      };
    }
    throw new Error(`unable to get coordinates from object: ${JSON.stringify(coordinates)} `);
  }
}

export function selectTripLegModelsForTripId(tripId: string) {
  return createSelector(
    selectWaypointsWithLatLngsByTripId(tripId),
    selectNonCancelledTripLegsByTripId(tripId),
    selectCurrentTripLegByTripId(tripId),
    selectVehicleDispositionByTripId(tripId),
    selectEstimatedDurationsByTripId(tripId),
    (waypoints, tripLegs, currentTripLeg, vehicleDisposition, durations) => {
      return tripLegs
        .sortBy((l) => l.sequence)
        .map((l) => ({
          tripLeg: l,
          startingWaypoint:
            l.startingWaypointId == null ? undefined : waypoints.get(l.startingWaypointId),
          endingWaypoint:
            l.endingWaypointId == null ? undefined : waypoints.get(l.endingWaypointId),
        }))
        .filter(
          (data) =>
            // a leg may have a waypoint id, but the waypoint may not exist in the waypoints list
            // because it does not have a lat/lng value.
            data.startingWaypoint != null && data.endingWaypoint != null,
        )
        .map((data) => {
          const isNextAtWaypoint = isAtWaypoint(
            getNextInSequence(tripLegs, data.tripLeg),
            vehicleDisposition,
          );
          const vm = new TripLegViewModelRecord({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            startingWaypoint: data.startingWaypoint!,
            tripLeg: data.tripLeg,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            endingWaypoint: data.endingWaypoint!,
            isCurrent: currentTripLeg != null && data.tripLeg.id === currentTripLeg.id,
            isNext:
              isAtWaypoint(data.tripLeg, vehicleDisposition) ||
              isNextInSequence(data.tripLeg, currentTripLeg),
            isNextAtWaypoint,
            isLast: data.tripLeg.sequence === tripLegs.count() - 1,
            isFirst: data.tripLeg.sequence === 0,
            officialDuration:
              data.tripLeg.officialEteId == null
                ? undefined
                : durations.get(data.tripLeg.officialEteId),
            calculatedDuration:
              data.tripLeg.calculatedEteId == null
                ? undefined
                : durations.get(data.tripLeg.calculatedEteId),
          });
          return vm;
        });
    },
  );
}

export function isAtWaypoint(
  tripLeg: TripLegRecord | undefined,
  vehicleDisposition: VehicleDispositionRecord | undefined,
) {
  return (
    tripLeg != null &&
    vehicleDisposition != null &&
    vehicleDisposition.currentWaypointId != null &&
    vehicleDisposition.currentWaypointId === tripLeg.startingWaypointId
  );
}

export function isNextInSequence(
  tripLeg: TripLegRecord,
  currentTripLeg: TripLegRecord | undefined,
) {
  return currentTripLeg != null && tripLeg.sequence === 1 + currentTripLeg.sequence;
}

export function getNextInSequence(
  tripLegs: Seq.Indexed<TripLegRecord>,
  currentTripLeg: TripLegRecord | undefined,
) {
  return currentTripLeg == null
    ? undefined
    : tripLegs.find((l) => l.sequence === currentTripLeg.sequence + 1);
}

export const legViewModel = (
  startingWaypoint: WaypointRecord,
  endingWaypoint: WaypointRecord,
  status: TripLegStatus,
) => {
  if (!hasLatLng(startingWaypoint) || !hasLatLng(endingWaypoint)) {
    return null;
  }
  return new TripLegViewModelRecord({
    startingWaypoint,
    endingWaypoint,
    tripLeg: new TripLegRecord({
      id: 'fake',
      sequence: 0,
      dateLastUpdated: new Date(),
      dateCreated: new Date(),
      dateDeleted: null,
      companyId: 'fake',
      status,
      tripId: 'fake',
      startingWaypointId: startingWaypoint.id,
      endingWaypointId: endingWaypoint.id,
      calculatedEteId: null,
      officialEteId: null,
      estimatedDistanceInMiles: null,
      arrivingOdometerReading: null,
      arrivingNote: null,
      departingOdometerReading: null,
      departingNote: null,
      lastUpdatedById: null,
      isEmergent: null,
      abortDate: null,
      encodedPolyline: null,
      dateOnFinalMarked: null,
    }),
    isCurrent: false,
    isNext: false,
    isNextAtWaypoint: false,
    isLast: false,
    isFirst: false,
    officialDuration: undefined,
    calculatedDuration: undefined,
  });
};
